import React, { useState, useEffect, memo } from 'react';
import { Route, useHistory, useLocation, matchPath } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import store from '@Store';
import NavigationHistory from '@Services/NavigationHistory';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { closeDialog } from '@Containers/Dialogs/DialogsActions';
import { typeOfTitlesAbstractsModal } from '@Containers/TitlesAbstractsModal/TitlesAbstractsModalConstants';
import { getCombinedRouteParams, getTrackingPathName } from './AppRoutesSelectors';
import { setCombinedRouteParams } from './AppRoutesActions';
import { sourceKeyMapping, paths } from './constants';

// Compares combinedRouteParams (from Redux store) to determine if the component should update.
// Prevents re-render when path and combinedRouteParams stay the same.
const MemoizedRoute = connect(
  (state) => ({
    combinedRouteParams: getCombinedRouteParams(state),
  }),
  null
)(
  memo(
    Route,
    (prevProps, nextProps) =>
      prevProps.path === nextProps.path &&
      prevProps.combinedRouteParams === nextProps.combinedRouteParams &&
      !nextProps.path.includes(paths.RS)
  )
);

/*
 * TODO ConnectedRoute requires serious reworking or removal.
 *  When navigation from Quick Search to Results, it gets re-mounted, pathname and params from match are correct in this case,
 *  But when navigation from Query Builder to Results, it remains mounted, pathname remains /search/advanced and params from match are empty.
 * This causes issue with filters, like RX-17313, RX-17315 and RX-17330
 * */
export default function ConnectedRoute(props) {
  const [isInitialRender, setIsInitialRender] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const dispatchSetCombinedRouteParams = (_location) => {
    const { state: locationState, pathname, search: searchStr } = _location;
    const match = matchPath(pathname, props);

    // Exact match, handles e.g. main.profile.account, main.profile.profile, main.profile.preferences
    const exactMatchPathKey = Object.keys(sourceKeyMapping).find((path) => matchPath(pathname, { path, exact: true }));
    // Non-exact match handles e.g. main.rs
    const notExactMatchPathKey = Object.keys(sourceKeyMapping).find((path) => matchPath(pathname, { path, exact: false }));
    // Prefer exact match if both exist
    const trackingPathKey = exactMatchPathKey || notExactMatchPathKey;
    const trackingPathName = sourceKeyMapping[trackingPathKey] || pathname;

    const routeParams = get(match, 'params', {});
    const locationSearch = Object.fromEntries(new URLSearchParams(searchStr));
    dispatch(setCombinedRouteParams({ locationState, routeParams, locationSearch, trackingPathName }));
  };

  useEffect(() => {
    // Set params for initial location
    dispatchSetCombinedRouteParams(location);
    setIsInitialRender(false);
    // tracking loadtime initiate for page change
    RouteChangeTracking.trackOnRouteChangeStart();
    dispatch(
      closeDialog({
        id: typeOfTitlesAbstractsModal,
      })
    );
    // Subscribe to history changes
    const historyUnlisten = history.listen((_location) => {
      dispatchSetCombinedRouteParams(_location);
    });

    return () => {
      historyUnlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Prevent initial render with empty params
  return isInitialRender ? null : <MemoizedRoute {...props} />;
}

export function withCombinedRouteParams(Component) {
  function WithCombinedRouteParamsHOC(props) {
    const combinedRouteParams = useSelector((state) => getCombinedRouteParams(state));
    return <Component {...props} {...combinedRouteParams} />;
  }
  return WithCombinedRouteParamsHOC;
}

export function withCombinedRouteParamsforResults(Component) {
  function WithCombinedRouteParamsforResultHOC(props) {
    let params = useSelector((state) => getCombinedRouteParams(state));
    const { match } = props;
    /** in case of browser back location.state is not persisting so we need this check */
    if (!params || Object.keys(params).length === 0) {
      params = { ...match.params };
    }
    const combinedParams = getCombinedRouteParams(store.getState());
    const trackingPathName = getTrackingPathName(store.getState());
    NavigationHistory.addState(trackingPathName, combinedParams);
    return <Component {...props} params={params} />;
  }
  return WithCombinedRouteParamsforResultHOC;
}

export function withCombinedRouteParamsforHeatmapResults(Component) {
  function WithCombinedRouteParamsforResultHOC(props) {
    let params = useSelector((state) => getCombinedRouteParams(state));
    const { match } = props;
    /** in case of browser back location.state is not persisting so we need this check */
    if (!params || Object.keys(params).length === 0) {
      params = { ...match.params, isHeatmap: true };
    }
    const combinedParams = getCombinedRouteParams(store.getState());
    const trackingPathName = getTrackingPathName(store.getState());
    NavigationHistory.addState(trackingPathName, combinedParams);
    return <Component {...props} params={params} />;
  }
  return WithCombinedRouteParamsforResultHOC;
}
